// SEO Utilities and Meta Tag Management
import { useEffect } from 'react';

export interface SEOMetaData {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonical?: string;
  robots?: string;
}

/**
 * Custom hook to manage SEO meta tags dynamically
 */
export const useSEO = (meta: SEOMetaData) => {
  useEffect(() => {
    // Update title
    document.title = meta.title;

    // Helper function to set meta tag
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      if (!content) return;

      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }

      element.setAttribute('content', content);
    };

    // Helper function to set link tag
    const setLinkTag = (rel: string, href: string) => {
      if (!href) return;

      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;

      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }

      element.href = href;
    };

    // Primary Meta Tags
    setMetaTag('description', meta.description);
    if (meta.keywords) setMetaTag('keywords', meta.keywords);
    if (meta.robots) setMetaTag('robots', meta.robots);

    // Open Graph Tags
    setMetaTag('og:type', 'website', true);
    setMetaTag('og:title', meta.ogTitle || meta.title, true);
    setMetaTag('og:description', meta.ogDescription || meta.description, true);
    if (meta.ogImage) setMetaTag('og:image', meta.ogImage, true);
    if (meta.ogUrl) setMetaTag('og:url', meta.ogUrl, true);

    // Twitter Card Tags
    setMetaTag('twitter:card', meta.twitterCard || 'summary_large_image', true);
    setMetaTag('twitter:title', meta.twitterTitle || meta.title, true);
    setMetaTag('twitter:description', meta.twitterDescription || meta.description, true);
    if (meta.twitterImage) setMetaTag('twitter:image', meta.twitterImage, true);

    // Canonical URL
    if (meta.canonical) {
      setLinkTag('canonical', meta.canonical);
    }
  }, [meta]);
};

/**
 * Add structured data script tag to document head (non-hook version)
 * Use this inside useEffect only!
 */
export const addStructuredData = (schema: any) => {
  if (typeof document === 'undefined') return;

  const scriptId = 'structured-data-' + (schema['@type'] || 'generic');
  let script = document.getElementById(scriptId) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(schema);
};

/**
 * Remove structured data script tag (call on cleanup)
 */
export const removeStructuredData = (schemaType: string) => {
  if (typeof document === 'undefined') return;
  const scriptId = 'structured-data-' + schemaType;
  const script = document.getElementById(scriptId);
  if (script) {
    script.remove();
  }
};

/**
 * Default SEO configuration
 */
export const DEFAULT_SEO: SEOMetaData = {
  title: 'NescoTech | Future-Ready Digital Agency',
  description: 'Full-service digital agency specializing in web development, mobile apps, AI solutions, and digital marketing. 250+ happy clients worldwide. Get your free quote today.',
  keywords: 'web development, mobile apps, AI solutions, digital marketing, SEO, branding, e-commerce, UI/UX design',
  ogImage: '/og-image.jpg',
  ogUrl: 'https://nescotech.digital/',
  twitterCard: 'summary_large_image',
  canonical: 'https://nescotech.digital/',
  robots: 'index, follow'
};

/**
 * Page-specific SEO configurations
 */
export const PAGE_SEO = {
  home: {
    title: 'NescoTech | Future-Ready Digital Agency',
    description: 'Full-service digital agency specializing in web development, mobile apps, AI solutions, and digital marketing. 250+ happy clients worldwide.',
    keywords: 'digital agency, web development, mobile apps, AI solutions, digital marketing',
    canonical: 'https://nescotech.digital/'
  },
  services: {
    title: 'Our Services | NescoTech',
    description: 'Explore our comprehensive digital services: web development, mobile apps, AI automation, digital marketing, branding, and more. Custom solutions for your business.',
    keywords: 'web development services, mobile app development, AI automation, digital marketing services, branding',
    canonical: 'https://nescotech.digital/services'
  },
  portfolio: {
    title: 'Portfolio | NescoTech',
    description: 'View our portfolio of successful projects including web applications, mobile apps, AI solutions, and digital marketing campaigns.',
    keywords: 'portfolio, case studies, web projects, mobile apps, success stories',
    canonical: 'https://nescotech.digital/portfolio'
  },
  booking: {
    title: 'Book a Consultation | NescoTech',
    description: 'Schedule a free consultation with our experts. Get a custom quote for your web development, mobile app, or digital marketing project.',
    keywords: 'book consultation, free quote, project estimate, consultation',
    canonical: 'https://nescotech.digital/booking'
  },
  blog: {
    title: 'Blog | NescoTech',
    description: 'Latest insights on web development, mobile apps, AI, digital marketing, and technology trends. Expert tips and industry news.',
    keywords: 'tech blog, web development blog, digital marketing tips, AI news',
    canonical: 'https://nescotech.digital/blog'
  },
  about: {
    title: 'About Us | NescoTech',
    description: 'Learn about NescoTech - a team of expert developers, designers, and marketers dedicated to building future-ready digital solutions.',
    keywords: 'about us, our team, company info, digital agency team',
    canonical: 'https://nescotech.digital/about'
  },
  contact: {
    title: 'Contact Us | NescoTech',
    description: 'Get in touch with NescoTech. Contact our team for inquiries, support, or to discuss your next project.',
    keywords: 'contact, get in touch, support, inquiries',
    canonical: 'https://nescotech.digital/contact'
  }
};
